import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { UserPhoneNumbersQueryKey } from '../../queryKey';
import { fetchUserPhoneNumberVerify } from './service';
import { UseUserPhoneNumberVerifyProps } from './props';
import {
  UserPhoneNumberVerifyInputDto,
  UserPhoneNumberVerifyOutputDto,
} from '@starsoft/common/dto';
import { KYCQueryKeys } from '@starsoft/common/api';
import { NotificationsService } from '@starsoft/common/services';
import { useNotificationService } from '@starsoft/common/hooks/useNotificationService';
import { useAxiosInstance } from '@starsoft/common/hooks';
import { AxiosInstance } from 'axios';

/**
 * Custom hook to verify a user's phone number
 *
 * This hook handles verifying a phone number with a verification code, managing the mutation state,
 * and handling side effects like cache invalidation and success notifications after verification.
 * It uses React Query's mutation capabilities for state management.
 *
 * @param {UseUserPhoneNumberVerifyProps} props - Optional configuration object
 * @param {boolean} props.isCreating - Flag indicating if this verification is part of phone number creation
 *
 * @returns {Object} An object containing:
 *   - verifyUserPhoneNumber: Function to trigger the verification mutation
 *     @param {UserPhoneNumberVerifyInputDto} data - The verification data including code
 *     @returns {Promise<UserPhoneNumberVerifyOutputDto>} The verification response
 *   - isPending: Boolean indicating if verification is in progress
 */
export function useUserPhoneNumberVerify(
  props?: UseUserPhoneNumberVerifyProps,
) {
  const { isCreating } = props ?? {};
  const queryClient: QueryClient = useQueryClient();
  const NotificationsService: NotificationsService<unknown> =
    useNotificationService();
  const axiosInstance: AxiosInstance = useAxiosInstance();

  /**
   * Handles side effects after successful phone number verification
   * - Invalidates phone numbers queries to trigger refetch
   * - Invalidates KYC status query
   * - Shows success notification with appropriate message
   *
   * @param {UserPhoneNumberVerifyOutputDto} userPhoneNumberOutput - The verification response data
   */
  function onSuccess(userPhoneNumberOutput: UserPhoneNumberVerifyOutputDto) {
    queryClient.invalidateQueries({
      queryKey: [UserPhoneNumbersQueryKey.Me],
    });
    queryClient.invalidateQueries({
      queryKey: [
        UserPhoneNumbersQueryKey.ById,
        userPhoneNumberOutput.phoneNumber.id,
      ],
    });
    queryClient.invalidateQueries({ queryKey: [KYCQueryKeys.Me] });
    NotificationsService.success({
      title: isCreating
        ? 'create_and_verify_phone_number_title'
        : 'verify_phone_number_title',
      message: isCreating
        ? 'create_and_verify_phone_number_description'
        : 'verify_phone_number_description',
      params: {
        phoneNumber: `${userPhoneNumberOutput.phoneNumber.country.dialCode} ${userPhoneNumberOutput.phoneNumber.maskedPhoneNumber}`,
      },
    });
  }

  const { mutate, isPending } = useMutation({
    mutationFn: (data: UserPhoneNumberVerifyInputDto) =>
      fetchUserPhoneNumberVerify(data, axiosInstance),
    onSuccess,
  });

  return {
    verifyUserPhoneNumber: mutate,
    isPending,
  };
}
